import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errorText } from "../../lib/api";
import { playNewOrderSound, unlockAudio } from "../../lib/sound";
import OwnerStats from "./OwnerStats";
import OrderFilters from "./OrderFilters";
import OwnerOrderCard from "./OwnerOrderCard";

const POLL_MS = 10000;

export default function OwnerOrdersPanel() {
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "all", payment_status: "paid", order_type: "all", q: "" });
    const [soundOn, setSoundOn] = useState(localStorage.getItem("owner_sound") !== "off");
    const lastPaid = useRef(null);
    const soundRef = useRef(soundOn);
    soundRef.current = soundOn;

    const loadOrders = useCallback(() => {
        const params = { ...filters, q: filters.q.trim() || undefined };
        return api.get("/owner/orders", { params })
            .then((r) => setOrders(r.data || []))
            .catch(() => toast.error("تعذر تحميل الطلبات"))
            .finally(() => setLoading(false));
    }, [filters]);

    const loadStats = useCallback(() => {
        return api.get("/owner/stats").then((r) => {
            const s = r.data;
            if (lastPaid.current !== null && s.paid_count > lastPaid.current) {
                toast.success("وصل طلب جديد مدفوع!", { duration: 6000 });
                if (soundRef.current) playNewOrderSound();
            }
            lastPaid.current = s.paid_count;
            setStats(s);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(loadOrders, filters.q ? 300 : 0);
        return () => clearTimeout(t);
    }, [loadOrders, filters.q]);

    useEffect(() => {
        loadStats();
        const t = setInterval(() => {
            loadStats();
            loadOrders();
        }, POLL_MS);
        return () => clearInterval(t);
    }, [loadStats, loadOrders]);

    const toggleSound = () => {
        const next = !soundOn;
        setSoundOn(next);
        localStorage.setItem("owner_sound", next ? "on" : "off");
        if (next) {
            unlockAudio();
            playNewOrderSound();
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/owner/orders/${id}`, { status });
            toast.success("تم تحديث حالة الطلب");
            loadOrders();
            loadStats();
        } catch (e) {
            toast.error(errorText(e, "تعذر التحديث"));
        }
    };

    const syncPayment = async (id) => {
        try {
            await api.post(`/owner/orders/${id}/sync-payment`);
            toast.success("تم تحديث حالة الدفع");
            loadOrders();
            loadStats();
        } catch (e) {
            toast.error(errorText(e, "تعذر تحديث حالة الدفع"));
        }
    };

    return (
        <div data-testid="owner-orders-panel" onClick={unlockAudio}>
            <div className="flex items-center justify-between gap-2 mb-4">
                <div className="text-xs text-text-muted flex items-center gap-1.5" data-testid="auto-refresh-indicator">
                    <span className="w-2 h-2 rounded-full bg-[var(--mint)] animate-pulse" /> تحديث تلقائي كل 10 ثوانٍ
                </div>
                <button
                    onClick={toggleSound}
                    data-testid="owner-sound-toggle"
                    className={`text-sm font-bold px-3 py-1.5 rounded-xl border-2 transition ${
                        soundOn ? "border-brand text-brand bg-white" : "border-[var(--border-subtle)] text-text-muted bg-white"
                    }`}
                >
                    <i className={`fa-solid ${soundOn ? "fa-bell" : "fa-bell-slash"} ms-1`} />
                    {soundOn ? "التنبيه الصوتي مفعل" : "التنبيه الصوتي متوقف"}
                </button>
            </div>

            <OwnerStats stats={stats} />
            <OrderFilters filters={filters} setFilters={setFilters} />

            {loading && (
                <div className="text-center text-text-muted py-8">
                    <i className="fa-solid fa-spinner fa-spin text-brand text-2xl" />
                </div>
            )}

            {!loading && orders.length === 0 && (
                <div className="text-center text-text-muted py-12 surface-card" data-testid="owner-orders-empty">
                    لا توجد طلبات مطابقة
                </div>
            )}

            <div className="grid gap-3 lg:grid-cols-2" data-testid="owner-orders-list">
                {orders.map((o) => (
                    <OwnerOrderCard key={o.id} order={o} onStatus={updateStatus} onSync={syncPayment} />
                ))}
            </div>
        </div>
    );
}
